/**
 * Payments overview — recent transactions with filters, refund modal,
 * and CSV export.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Download, Receipt } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { fetchAll, LOG_MAX_ROWS } from "@/lib/fetchAll";
import { TruncationNotice } from "@/components/TruncationNotice";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/data-table/DataTable";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/payments/")({ component: PaymentsPage });

interface PaymentRow {
  id: number;
  order_id: string;
  razorpay_payment_id: string | null;
  amount_inr: number;
  status: "created" | "authorized" | "captured" | "refunded" | "failed" | "cancelled";
  payer_name: string | null;
  payer_email: string | null;
  related_resource: string | null;
  related_id: number | null;
  created_at: number;
  captured_at: number | null;
  category_name: string | null;
}

const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

function statusBadge(s: PaymentRow["status"]) {
  const map: Record<PaymentRow["status"], { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive" | "outline" }> = {
    created:    { label: "Created",    variant: "secondary" },
    authorized: { label: "Authorized", variant: "warning"   },
    captured:   { label: "Captured",   variant: "success"   },
    refunded:   { label: "Refunded",   variant: "outline"   },
    failed:     { label: "Failed",     variant: "destructive" },
    cancelled:  { label: "Cancelled",  variant: "outline"   },
  };
  const v = map[s];
  return <Badge variant={v.variant}>{v.label}</Badge>;
}

function PaymentsPage() {
  const { can } = useAuth();
  const [refundOpenFor, setRefundOpenFor] = useState<PaymentRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["payments", "list"],
    // Transactions only accumulate, so bound the window rather than page the
    // whole ledger. Full history is available via the CSV export above.
    queryFn: () => fetchAll<PaymentRow>("/admin/payments", { maxRows: LOG_MAX_ROWS }),
  });

  const columns: ColumnDef<PaymentRow, unknown>[] = [
    {
      accessorKey: "captured_at", header: "When",
      cell: ({ row }) => format((row.original.captured_at ?? row.original.created_at) * 1000, "MMM d, HH:mm"),
    },
    {
      id: "payer", header: "Payer",
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.payer_name ?? "—"}</p>
          <p className="text-xs text-muted-foreground">{row.original.payer_email}</p>
        </div>
      ),
    },
    {
      accessorKey: "amount_inr", header: "Amount",
      cell: ({ row }) => <span className="font-mono">₹{(row.original.amount_inr / 100).toLocaleString("en-IN")}</span>,
    },
    {
      accessorKey: "category_name", header: "Category",
      cell: ({ row }) => row.original.category_name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      id: "for", header: "For",
      cell: ({ row }) => row.original.related_resource
        ? <span className="text-xs text-muted-foreground">{row.original.related_resource} #{row.original.related_id}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      accessorKey: "status", header: "Status",
      cell: ({ row }) => statusBadge(row.original.status),
      filterFn: (r, _i, v) => r.getValue("status") === v,
    },
    {
      id: "actions", header: "",
      cell: ({ row }) => row.original.status === "captured" && (
        <Button
          variant="ghost" size="sm"
          disabled={!can("payments", "refund")}
          onClick={() => setRefundOpenFor(row.original)}
        >
          <Receipt className="h-3.5 w-3.5" /> Refund
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <PageHeader
        title="Payments"
        description="All Razorpay transactions captured by the platform."
        actions={can("payments", "export") && (
          <Button variant="outline" size="sm" asChild>
            <a href={`${apiBase}/admin/payments/export.csv`} download>
              <Download className="h-4 w-4" /> CSV
            </a>
          </Button>
        )}
      />
      {data?.truncated && <TruncationNotice count={data.items.length} noun="payments" />}
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search by payer, order id, amount…"
        pageSize={50}
        filters={[{ id: "status", label: "Status", options: [
          { value: "created", label: "Created" }, { value: "authorized", label: "Authorized" }, { value: "captured", label: "Captured" },
          { value: "refunded", label: "Refunded" }, { value: "failed", label: "Failed" }, { value: "cancelled", label: "Cancelled" },
        ]}]}
        emptyMessage={isLoading ? "Loading…" : "No payments yet."}
      />

      {refundOpenFor && (
        <RefundDialog payment={refundOpenFor} onClose={() => setRefundOpenFor(null)} />
      )}
    </div>
  );
}

const refundSchema = z.object({
  amount_rupees: z.coerce.number().positive(),
  reason: z.string().max(500).optional(),
});
type RefundFormValues = z.infer<typeof refundSchema>;

function RefundDialog({ payment, onClose }: { payment: PaymentRow; onClose: () => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, formState: { errors } } = useForm<RefundFormValues>({
    resolver: zodResolver(refundSchema),
    defaultValues: { amount_rupees: payment.amount_inr / 100, reason: "" },
  });

  const refund = useMutation({
    mutationFn: (v: RefundFormValues) => api.post(`/admin/payments/${payment.id}/refund`, {
      amount_inr: Math.round(v.amount_rupees * 100),
      reason: v.reason,
    }),
    onSuccess: () => { toast.success("Refund issued"); qc.invalidateQueries({ queryKey: ["payments"] }); onClose(); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Refund failed"),
  });

  return (
    <Dialog open onOpenChange={(b) => !b && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refund payment</DialogTitle>
          <DialogDescription>
            Razorpay will refund to the original payment method. Settles within 5–7 business days.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={handleSubmit((v) => refund.mutate(v))}>
          <div className="rounded-md border bg-secondary/30 p-3 text-sm">
            <p><span className="text-muted-foreground">Order:</span> <code className="text-xs">{payment.order_id}</code></p>
            <p><span className="text-muted-foreground">Payer:</span> <strong>{payment.payer_name ?? "—"}</strong> ({payment.payer_email})</p>
            <p><span className="text-muted-foreground">Original:</span> ₹{(payment.amount_inr / 100).toLocaleString("en-IN")}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="amount_rupees">Refund amount (₹)</Label>
            <Input id="amount_rupees" type="number" step="0.01" min={0.01} {...register("amount_rupees")} />
            {errors.amount_rupees && <p className="text-xs text-destructive">{errors.amount_rupees.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Input id="reason" placeholder="Event cancelled, duplicate, etc." {...register("reason")} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={refund.isPending}>{refund.isPending ? "Refunding…" : "Issue refund"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
